import React from 'react'
import { connect } from 'react-redux'
import { Button } from 'reactstrap'
import PropTypes from 'prop-types'
import { v1 as uuidv1 } from 'uuid'
import { withTranslation } from 'react-i18next'

import AlertMessage from '../../components/AlertMessage'
import integrationsActions from '../../integrations/actions'
import LinkToIntegrationModal from './LinkToIntegrationModal'
import NoConfiguredIntegration from './NoConfiguredIntegration'
import PageLoader from '../../components/PageLoader'
import localesActions from '../../localez/actions'
import ProjectCard from './ProjectCard'
import ProjectCreationCard from './ProjectCreationCard'
import ProjectCreationModal from './ProjectCreationModal'
import ProjectDeleteConfirmModal from './ProjectDeleteConfirmModal'
import ProjectEditModal from './ProjectEditModal'
import ProjectFilters from './ProjectFilters'
import ProjectForceImportConfirmModal from './ProjectForceImportConfirmModal'
import ProjectForceReImportConfirmModal from './ProjectForceReImportConfirmModal'
import projectsActions from './actions'

class ProjectDeck extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      activeFilter: null,
      selectedProjects: [],
      showLinkToIntegrationModal: false,
      target: null,
      showProjectCreationModal: false,
      showProjectDeleteConfirmModal: false,
      showProjectEditModal: false,
      showProjectForceImportModal: false,
      showProjectForceReImportModal: false
    }
  }

  componentDidMount() {
    const { dispatch } = this.props

    dispatch(integrationsActions.fetchAll())
    dispatch(localesActions.fetchAll())
    dispatch(projectsActions.fetchAll())
  }

  setShowProjectCreationModal(showProjectCreationModal) {
    this.setState({
      showProjectCreationModal
    })
  }

  setShowProjectEditModal(showProjectEditModal) {
    this.setState({
      showProjectEditModal
    })
  }

  hideForceImportConfirmModal() {
    this.setState({
      target: null,
      showProjectForceImportModal: false
    })
  }

  hideDeleteConfirmModal() {
    this.setState({
      target: null,
      showProjectDeleteConfirmModal: false
    })
  }

  showDeleteConfirmFor(project) {
    this.setState({
      target: project,
      showProjectDeleteConfirmModal: true
    })
  }

  showEditModalFor(project) {
    this.setState({
      target: project,
      showProjectEditModal: true
    })
  }

  showForceImportModalFor(project) {
    this.setState({
      target: project,
      showProjectForceImportModal: true
    })
  }

  hideForceReImportConfirmModal() {
    this.setState({
      target: null,
      showProjectForceReImportModal: false
    })
  }

  showForceReImportModalFor(project) {
    this.setState({
      target: project,
      showProjectForceReImportModal: true
    })
  }

  handleFilterChange = (filterKey) => {
    this.setState({
      activeFilter: filterKey,
      selectedProjects: [] // Clear selection when filter changes
    })
  }

  handleProjectSelect = (projectId, isSelected) => {
    this.setState((prevState) => {
      if (isSelected) {
        return { selectedProjects: [...prevState.selectedProjects, projectId] }
      }
      return {
        selectedProjects: prevState.selectedProjects.filter((id) => id !== projectId)
      }
    })
  }

  filterProjects = (projects) => {
    const { activeFilter } = this.state

    if (!activeFilter) return projects

    return projects.filter((project) => {
      switch (activeFilter) {
        case 'failed':
          return project.state === 'importing_failed'
        case 'conflicts':
          return project.strings_stats?.conflicts > 0
        case 'missing':
          return project.strings_stats?.missing_locale_strings > 0
        case 'orphans':
          return project.is_orphaned
        default:
          return true
      }
    })
  }

  showLinkToIntegrationModal = () => {
    this.setState({ showLinkToIntegrationModal: true })
  }

  hideLinkToIntegrationModal = () => {
    this.setState({
      showLinkToIntegrationModal: false,
      selectedProjects: []
    })
  }

  render() {
    const {
      integrations,
      locales,
      projectsAllFetchingError,
      projectsAllFetchingStatus,
      projects,
      t
    } = this.props

    const {
      activeFilter,
      selectedProjects,
      showLinkToIntegrationModal,
      target,
      showProjectCreationModal,
      showProjectDeleteConfirmModal,
      showProjectEditModal,
      showProjectForceImportModal,
      showProjectForceReImportModal
    } = this.state

    if (projectsAllFetchingStatus === 'fetching') {
      return (
        <div className="d-flex flex-column">
          <PageLoader
            message={t('ProjectDeck.loading')}
          />
        </div>
      )
    }

    if (projectsAllFetchingStatus === 'failed') {
      return (
        <div className="d-flex flex-column">
          <AlertMessage>
            {typeof projectsAllFetchingError === 'string'
              ? projectsAllFetchingError
              : t('errors.somethingWentWrong', { ns: 'commons' })}
          </AlertMessage>
        </div>
      )
    }

    const filteredProjects = this.filterProjects(projects || [])
    const hasNoIntegrations = !integrations || integrations.length === 0
    const hasNoProjects = !projects || projects.length === 0

    // Show NoConfiguredIntegration only when there are no integrations AND no projects
    if (hasNoIntegrations && hasNoProjects) {
      return <NoConfiguredIntegration />
    }

    return (
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {activeFilter === 'orphans' && selectedProjects.length > 0 ? (
            <div className="d-flex align-items-center gap-2">
              <span>
                {t('ProjectDeck.selectedCount', { count: selectedProjects.length })}
              </span>
              <Button
                color="primary"
                size="sm"
                onClick={() => this.showLinkToIntegrationModal()}
              >
                {t('ProjectDeck.buttons.linkToIntegration')}
              </Button>
              <Button
                color="secondary"
                size="sm"
                onClick={() => this.setState({ selectedProjects: [] })}
              >
                {t('ProjectDeck.buttons.clearSelection')}
              </Button>
            </div>
          ) : (
            <div />
          )}
          <ProjectFilters
            activeFilter={activeFilter}
            onFilterChange={this.handleFilterChange}
          />
        </div>

        {activeFilter && filteredProjects.length === 0 ? (
          <div
            className="d-flex flex-fill align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <p className="text-muted">{t('ProjectDeck.noResults')}</p>
          </div>
        ) : (
          <div className="d-flex flex-wrap">
            {locales && filteredProjects.map((project) => (
              <ProjectCard
                key={uuidv1()}
                integration={integrations?.find((i) => i.id === project.integration_id)}
                isSelected={selectedProjects.includes(project.id)}
                onClickDelete={(selected) => this.showDeleteConfirmFor(selected)}
                onClickEdit={(selected) => this.showEditModalFor(selected)}
                onClickForceImport={(selected) => this.showForceImportModalFor(selected)}
                onClickForceReImport={(selected) => this.showForceReImportModalFor(selected)}
                onSelect={(isSelected) => this.handleProjectSelect(project.id, isSelected)}
                projectId={project.id}
                showCheckbox={activeFilter === 'orphans'}
              />
            ))}

            {!activeFilter && !hasNoIntegrations && (
              <ProjectCreationCard
                onClick={() => this.setShowProjectCreationModal(true)}
                title={t('ProjectDeck.buttons.createTitle')}
              />
            )}
          </div>
        )}

        <ProjectCreationModal
          isOpen={showProjectCreationModal}
          toggle={() => this.setShowProjectCreationModal(false)}
        />

        <ProjectEditModal
          isOpen={showProjectEditModal}
          project={showProjectEditModal ? target : {}}
          projectId={target?.id}
          toggle={() => this.setShowProjectEditModal(false)}
        />

        <ProjectDeleteConfirmModal
          id={target?.id}
          isOpen={showProjectDeleteConfirmModal}
          onClose={() => this.hideDeleteConfirmModal()}
        />

        <ProjectForceImportConfirmModal
          isOpen={showProjectForceImportModal}
          project={showProjectForceImportModal ? target : {}}
          onClose={() => this.hideForceImportConfirmModal()}
          projectId={target?.id}
          repositoryId={target?.repository_id}
        />

        <ProjectForceReImportConfirmModal
          isOpen={showProjectForceReImportModal}
          project={showProjectForceReImportModal ? target : {}}
          onClose={() => this.hideForceReImportConfirmModal()}
        />

        <LinkToIntegrationModal
          integrations={integrations}
          isOpen={showLinkToIntegrationModal}
          onClose={() => this.hideLinkToIntegrationModal()}
          projects={projects}
          selectedProjectIds={selectedProjects}
        />
      </div>
    )
  }
}

ProjectDeck.propTypes = {
  dispatch: PropTypes.func.isRequired,
  integrations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  })),
  locales: PropTypes.arrayOf(PropTypes.object),
  projectsAllFetchingError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  projectsAllFetchingStatus: PropTypes.string,
  projects: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    integration_id: PropTypes.string,
    is_orphaned: PropTypes.bool
  })),
  t: PropTypes.func.isRequired
}

ProjectDeck.defaultProps = {
  integrations: [],
  locales: null,
  projectsAllFetchingError: null,
  projectsAllFetchingStatus: 'idle',
  projects: null
}

const mapStateToProps = ({
  integrations: {
    items: integrations
  },
  locales: {
    items: locales
  },
  projects: {
    fetchingAllError: projectsAllFetchingError,
    fetchingAllStatus: projectsAllFetchingStatus,
    items: projects
  }
}) => ({
  integrations,
  locales,
  projectsAllFetchingError,
  projectsAllFetchingStatus,
  projects
})

export default withTranslation(['ProjectsPage', 'commons'])(
  connect(mapStateToProps)(ProjectDeck)
)
